import { ApplicationConfig, LOCALE_ID } from '@angular/core';
import { provideRouter } from '@angular/router';
import {
  dashboardProviders,
  provideKundeninfoWidget,
} from '@softapps/allgemein/dashboard';
import { withLogin } from '@softapps/allgemein/login';
import { moduleProviders } from '@softapps/allgemein/modules';
import { passwordProviders } from '@softapps/allgemein/password';
import {
  accountContextProviders,
  contextSettingsProviders,
  settingsProviders,
  userContextProviders,
  utilitiesProviders,
} from '@softapps/allgemein/utils';
import {
  provideSoftApps,
  SOFTLINE_ROUTES_APPS,
  SOFTLINE_ROUTES_PUBLIC,
  SOFTLINE_CONFIG_PUBLIC_SHELL_BRANDING,
} from '@softapps/core';
import { fibuCoreProviders, kundeninfoLeisteProviders } from '@softapps/fibu/core';
import { offenePostenProviders } from '@softapps/fibu/offene-posten';
import { gutscheinkartenProviders } from '@softapps/handel/gutscheinkarten';
import { artikelvorratProviders } from '@softapps/mde/artikelvorrat';
import { bestellerfassungProviders } from '@softapps/mde/bestellerfassung';
import { bestellvorschlagProviders } from '@softapps/mde/bestellvorschlag';
import { etikettendruckProviders } from '@softapps/mde/etikettendruck';
import { retourenProviders } from '@softapps/mde/retouren';
import { provideStorageLocation } from '@softapps/mde/storage-location';
import { warenverprobungProviders } from '@softapps/mde/warenverprobung';
import { provideRoster } from '@softapps/pms/roster';
import { statusabfrageProviders } from '@softapps/pms/statusabfrage';
import {
  pmsStempelTerminalProviders,
  SOFTLINE_CONFIG_PMS_STEMPELTERMINAL_CONFIG,
} from '@softapps/pms/stempel-terminal';
import { stempelerfassungProviders } from '@softapps/pms/stempelerfassung';
import { provideGarageCore } from '@softapps/werkstatt/garage-core';
import { provideGarageReport } from '@softapps/werkstatt/report';
import { withGarageRoster } from '@softapps/werkstatt/roster';
import { provideToolModule, provideVehicleModule } from '@softapps/werkstatt/vehicle';
import { artikelInfoProviders } from '@softapps/wws/artikel-info';
import { wwsCoreProviders } from '@softapps/wws/core';
import { energietransportProviders } from '@softapps/wws/energietransport';
import { kreditlimitProviders } from '@softapps/wws/kreditlimit';
import { kundenabfrageProviders } from '@softapps/wws/kundenabfrage';
import { umsatzstatistikProviders } from '@softapps/wws/umsatzstatistik';
import {
  provideDarkTheme,
  remoteConfigProviders,
  SOFTLINE_CONFIG_DEFAULT_THEME,
  SOFTLINE_CONFIG_THEMES,
} from '@softline/application';
import { environment } from '../environments/environment';
import { platform } from '../platforms/platform';
import { publicRoutes, shellRoutes } from './app.routes';
import { provideMdeInventoryModule } from '@softapps/mde/inventory';
import { provideGarageOrderModule } from '@softapps/werkstatt/order';
import { provideMachineCatalog } from '@softapps/werkstatt/machine-catalog';
import { provideEslConfigModule } from '@softapps/handel/esl-config';
import {
  provideEslLinkStorageProviders,
  provideEslSearchProviders,
} from '@softapps/handel/esl-link-storage';

export const appConfig: ApplicationConfig = {
  providers: [
    provideRouter([]),
    provideSoftApps({ connection: environment.connection }, withLogin()),

    { provide: SOFTLINE_CONFIG_DEFAULT_THEME, useValue: 'lagerhaus' },
    provideDarkTheme(false),
    {
      provide: SOFTLINE_CONFIG_THEMES,
      useValue: {
        key: 'lagerhaus',
        name: 'Lagerhaus',
      },
      multi: true,
    },

    { provide: SOFTLINE_ROUTES_APPS, useValue: shellRoutes, multi: true },
    { provide: SOFTLINE_ROUTES_PUBLIC, useValue: publicRoutes, multi: true },
    { provide: SOFTLINE_CONFIG_PUBLIC_SHELL_BRANDING, useValue: true },

    /*
    importProvidersFrom(
      BrowserAnimationsModule,
      RouterModule.forRoot(routes, { initialNavigation: 'enabledBlocking' }),

      // Framework
      CoreModule.forRoot(),
      StoreModule.forRoot(),
      ConnectionModule.forRoot(environment.connection),
      AuthenticationModule.forRoot(),
      JwtAuthenticationModule.forRoot(),
      ApplicationModule.forRoot(),
      AppearanceModule.forRoot(),
      DynamicModule.forRoot(),
      MasterDataModule.forRoot(),
      DeveloperModule.forRoot(),
      ConsoleModule.forRoot(),
      ItemScanModule.forRoot(),
      OfflineModule.forRoot(),
      SoftappsCoreModule.forRoot()
    ),
*/

    // Allgemein
    ...utilitiesProviders,
    ...contextSettingsProviders,
    ...userContextProviders,
    ...accountContextProviders,

    ...settingsProviders,
    ...remoteConfigProviders,
    ...passwordProviders,
    // ...loginProviders,
    ...moduleProviders,

    //Wws
    // ...gesamtabfrageProviders,
    ...stempelerfassungProviders,

    // MDE
    ...wwsCoreProviders,

    ...bestellvorschlagProviders,
    ...bestellerfassungProviders,
    ...etikettendruckProviders,
    ...provideMdeInventoryModule(),
    ...warenverprobungProviders,
    ...retourenProviders,
    ...artikelvorratProviders,
    ...provideStorageLocation(),

    ...artikelInfoProviders,

    ...statusabfrageProviders,
    provideEslConfigModule(),
    provideEslLinkStorageProviders({ storageSelection: false }),
    provideEslSearchProviders(),

    ...energietransportProviders,

    ...gutscheinkartenProviders,

    ...fibuCoreProviders,
    ...kundeninfoLeisteProviders,
    ...provideKundeninfoWidget(),
    ...dashboardProviders,
    ...kreditlimitProviders,
    ...umsatzstatistikProviders,

    ...kundenabfrageProviders,

    ...offenePostenProviders,

    // Werkstatt
    provideGarageReport(),
    provideRoster(withGarageRoster()),
    provideGarageCore(),
    provideVehicleModule(),
    provideToolModule(),
    provideGarageOrderModule(),
    provideMachineCatalog(),

    {
      provide: SOFTLINE_CONFIG_PMS_STEMPELTERMINAL_CONFIG,
      useValue: {
        dashboardInfo: {
          title: 'Lagerhaus Eferding',
          subtitle: 'Stempel-Terminal',
        },
        spezialStempelBemerkungPflicht: true,
      },
    },

    // PMS
    ...pmsStempelTerminalProviders,

    ...platform.providers,
    { provide: LOCALE_ID, useValue: 'de' },
  ],
};
